import React, {useContext, useEffect, useRef, useState} from 'react';
import {GlobalStateContext} from "../../../../components/general/GlobalStateProvider";
import {Image, Linking, Modal, Pressable, StyleSheet, Text, View} from "react-native";
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import DataOps from "../../../../data/DataOps";
import GenericEditModal from '../hosts/GenericEditModal';
import ShareModal from '../../../../components/general/ShareModal';
import { getCampTypeText } from '../../../../data/CampTypes';
import CampGeoHelper from '../../../../utility/CampGeoHelper';
import AddressAutocomplete from '../../../../components/general/AddressAutocomplete';

const CampPageLocation = ({camp, setCamp, isHost = false, isEditing = false, activeTab = 'General Information', onToggleView = null}) => {
    const {
        user: user,
        likedCamps: likedCamps,
        setLikedCamps: setLikedCamps,
        userCampId: userCampId
    } = useContext(GlobalStateContext);

    const [showAddressModal, setShowAddressModal] = useState(false);
    const [addressDraft, setAddressDraft] = useState('');
    const [addressError, setAddressError] = useState('');
    const [addressPartsDraft, setAddressPartsDraft] = useState<{
        street: string;
        city: string;
        state: string;
        zipcode: string;
        country?: string;
    } | null>(null);
    const [showWebsiteModal, setShowWebsiteModal] = useState(false);
    const [hoveredWebsite, setHoveredWebsite] = useState(false);
    const [hoveredAddress, setHoveredAddress] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const placesService = useRef<any>(null);

    // Check if current user is the host of this camp
    const isCurrentUserHost = userCampId === camp.campId;
    const canEdit = isEditing;

    const isLiked = likedCamps.has(camp.campId);

    const updateLikedCamps = () => {
        let newLikedCamps = new Set([...likedCamps]);
        if (isLiked) {
            newLikedCamps.delete(camp.campId);
        } else {
            newLikedCamps.add(camp.campId);
        }

        setLikedCamps(newLikedCamps);

        return newLikedCamps;
    }

    const updateCampFollowers = async () => {
        // Fetch again so that it's less likely for user to get outdated copy of camp followers.
        const campInfo = await DataOps.fetchDataFromFirebase('camp', camp.campId);
        const campFollowers = campInfo.campFollowers || [];

        let newCampFollowers = new Set([...campFollowers]);
        if (isLiked) {
            newCampFollowers.delete(user.userId);
        } else {
            newCampFollowers.add(user.userId);
        }

        await DataOps.saveCustomDataToFirebase(
            'camp', camp.campId, {...campInfo, campFollowers: [...newCampFollowers], followers: newCampFollowers.size}, true
        );
    }

    const handleLikePress = async () => {
        const newLikedCamps = await updateLikedCamps();

        await DataOps.saveCustomDataToFirebase(
            'user', user.userId,
            {...user, likedCamps: [...newLikedCamps]}, true);

        await updateCampFollowers();
    };

    const handleSharePress = () => {
        setShowShareModal(true);
    };

    const getShareUrl = () => {
        return `${window.location.origin}/camps/${camp.campId}`;
    };

    const getCampDescription = () => {
        const location = [camp.city, camp.state].filter(Boolean).join(', ');
        const category = getCampTypeText(camp.campCategory);
        
        // Count upcoming sessions
        const upcomingSessionsCount = camp.campSessions 
            ? Object.keys(camp.campSessions).filter(key => !camp.campSessions[key].deleted).length 
            : 0;
        
        return `${category} camp in ${location} · ${upcomingSessionsCount} sessions available`;
    };

    const ratingValue = Number(camp?.rating || 0);
    const reviewCount = Number(camp?.reviewCount || 0);
    const hasRatings = reviewCount > 0;
    const campLogoUrl = String(camp?.campLogoUrl || '').trim();

    const parseAddressFields = (addressText: string) => {
        const parts = String(addressText || '').split(',').map(part => part.trim()).filter(Boolean);
        const street = parts[0] || camp.street || '';
        const city = parts[1] || camp.city || '';

        let state = camp.state || '';
        let zipcode = camp.zipcode || '';
        if (parts[2]) {
            const stateZipMatch = parts[2].match(/^([A-Za-z]{2})(?:\s+(\d{5}(?:-\d{4})?))?$/);
            if (stateZipMatch) {
                state = stateZipMatch[1].toUpperCase();
                if (stateZipMatch[2]) {
                    zipcode = stateZipMatch[2];
                }
            } else {
                state = parts[2];
            }
        }

        if (!zipcode && parts[3]) {
            zipcode = parts[3];
        }

        return { street, city, state, zipcode };
    };

    useEffect(() => {
        if ((window as any)?.google && !placesService.current) {
            const dummy = document.createElement('div');
            placesService.current = new (window as any).google.maps.places.PlacesService(dummy);
        }
    }, []);

    const handleAddressSelect = (placeId: string, description: string) => {
        setAddressDraft(description);
        setAddressError('');
        if (!placesService.current) {
            setAddressPartsDraft(parseAddressFields(description));
            return;
        }

        placesService.current.getDetails(
            { placeId, fields: ['address_components', 'formatted_address'] },
            (place: any, status: any) => {
                if (status === (window as any).google.maps.places.PlacesServiceStatus.OK && place?.address_components) {
                    let streetNumber = '';
                    let route = '';
                    let city = '';
                    let state = '';
                    let zipcode = '';
                    let country = '';

                    place.address_components.forEach((component: any) => {
                        const types = component.types || [];
                        if (types.includes('street_number')) streetNumber = component.long_name;
                        if (types.includes('route')) route = component.long_name;
                        if (types.includes('locality')) city = component.long_name;
                        if (types.includes('administrative_area_level_1')) state = component.short_name;
                        if (types.includes('postal_code')) zipcode = component.long_name;
                        if (types.includes('country')) country = component.long_name;
                    });

                    setAddressDraft(place.formatted_address || description);
                    setAddressPartsDraft({
                        street: `${streetNumber} ${route}`.trim(),
                        city,
                        state,
                        zipcode,
                        country,
                    });
                } else {
                    setAddressPartsDraft(parseAddressFields(description));
                }
            }
        );
    };

    const openAddressModal = () => {
        setAddressDraft(camp.campAddress || [camp.street, camp.city, camp.state, camp.zipcode].filter(Boolean).join(', '));
        setAddressError('');
        setAddressPartsDraft(null);
        setShowAddressModal(true);
    };

    const saveAddress = () => {
        const address = String(addressDraft || '').trim();
        if (!address || address.length < 8) {
            setAddressError('Please enter a complete address.');
            return;
        }
        const parsedAddress = addressPartsDraft || parseAddressFields(address);
        const nextCampBase = {
            ...camp,
            campAddress: address,
            street: parsedAddress.street,
            city: parsedAddress.city,
            state: parsedAddress.state,
            zipcode: parsedAddress.zipcode,
            address: {
                street: parsedAddress.street,
                city: parsedAddress.city,
                state: parsedAddress.state,
                zipCode: parsedAddress.zipcode,
                country: parsedAddress.country || camp?.address?.country || camp?.country || 'USA',
            },
        };

        (async () => {
            const enriched = await CampGeoHelper.enrichCampWithGeolocation(nextCampBase);
            setCamp(prev => ({ ...prev, ...enriched }));
            try {
                await DataOps.saveCustomDataToFirebase('camp', camp.campId, {
                    ...enriched,
                    updatedAt: new Date().toISOString(),
                }, true);
            } catch (error) {
                console.error('Failed to persist camp address update:', error);
            }
        })();

        setShowAddressModal(false);
    };


    return (
        <View style={{flexDirection: 'row', marginTop: 0, marginBottom: 20, width: '100%'}}>
            <View style={{flexDirection: 'column'}}>
                <View style={{flexDirection: 'row', alignItems: 'center', marginBottom: 8}}>
                    {campLogoUrl ? (
                        <Image
                            source={{ uri: campLogoUrl }}
                            style={{
                                width: 50,
                                height: 50,
                                borderRadius: 12,
                                marginRight: 12,
                                borderWidth: 1,
                                borderColor: '#E5E7EB',
                                backgroundColor: '#F8FAFC',
                            }}
                        />
                    ) : null}
                    <h1 style={{ fontSize: 24, fontWeight: 'bold', margin: 0, marginRight: 10 }}>
                        {camp.campName}
                    </h1>
                    {
                        (camp.campWebsite || canEdit) &&
                        <Pressable 
                            onPress={() => {
                                if (canEdit) {
                                    setShowWebsiteModal(true);
                                } else if (camp.campWebsite) {
                                    Linking.openURL(camp.campWebsite);
                                }
                            }}
                            onHoverIn={() => {
                                if (canEdit) {
                                    setHoveredWebsite(true);
                                }
                            }}
                            onHoverOut={() => {
                                setHoveredWebsite(false);
                            }}
                            style={{
                                flexDirection: 'row', 
                                alignItems: 'center', 
                                marginTop: 4,
                                paddingHorizontal: 8,
                                paddingVertical: 4,
                                borderRadius: 4,
                                backgroundColor: hoveredWebsite ? '#f8f9fa' : 'transparent',
                                borderWidth: hoveredWebsite ? 1 : 0,
                                borderColor: hoveredWebsite ? '#007bff' : 'transparent',
                                position: 'relative',
                            }}
                        >
                            <MaterialCommunityIcons name={'link-variant'} size={14} color='#2C67D6'/>
                            <Text style={{marginLeft: 2, color: '#2C67D6', fontWeight: '500'}}>
                                {camp.campWebsite || 'camp website'}
                            </Text>
                            {hoveredWebsite && (
                                <MaterialCommunityIcons 
                                    name="pencil" 
                                    size={12} 
                                    color="#007bff" 
                                    style={{
                                        position: 'absolute',
                                        top: 2,
                                        right: 2,
                                    }}
                                />
                            )}
                        </Pressable>
                    }
                </View>

                <View style={{flexDirection: 'row', alignItems: 'center'}}>
                    <Pressable
                        onPress={() => {
                            if (canEdit) {
                                openAddressModal();
                            }
                        }}
                        onHoverIn={() => {
                            if (canEdit) {
                                setHoveredAddress(true);
                            }
                        }}
                        onHoverOut={() => setHoveredAddress(false)}
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            paddingHorizontal: 6,
                            paddingVertical: 4,
                            borderRadius: 6,
                            backgroundColor: hoveredAddress ? '#f8f9fa' : 'transparent',
                            borderWidth: hoveredAddress ? 1 : 0,
                            borderColor: hoveredAddress ? '#007bff' : 'transparent',
                        }}
                    >
                        <Text style={{fontSize: 16}}>
                            {
                                camp.campAddress || [camp.street, camp.city, camp.state, camp.zipcode].filter(Boolean).join(', ')
                            }
                        </Text>
                        {hoveredAddress && canEdit && (
                            <MaterialCommunityIcons name="pencil" size={12} color="#007bff" style={{ marginLeft: 8 }} />
                        )}
                    </Pressable>
                </View>

            </View>

            <View style={{flex: 1}}></View>

            <View style={{ alignItems: 'flex-end' }}>
                <View style={{flexDirection: 'row', alignItems: 'center', gap: 12}}>
                    {/* Share Button */}
                    <Pressable 
                        style={{
                            width: 48,
                            height: 48,
                            borderRadius: 24,
                            backgroundColor: '#F3F4F6',
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderWidth: 1,
                            borderColor: '#E5E7EB',
                        }}
                        onPress={handleSharePress}
                    >
                        <MaterialCommunityIcons name={'share-variant-outline'} size={24} color='#374151'/>
                    </Pressable>

                    {/* Follow/Like Button */}
                    <Pressable 
                        style={{
                            width: 48,
                            height: 48,
                            borderRadius: 24,
                            backgroundColor: isLiked ? '#FEE2E2' : '#F3F4F6',
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderWidth: 1,
                            borderColor: isLiked ? '#FECACA' : '#E5E7EB',
                        }}
                        onPress={handleLikePress}
                    >
                        <MaterialCommunityIcons 
                            name={isLiked ? 'heart' : 'heart-outline'} 
                            size={24}
                            color={isLiked ? '#EF4444' : '#374151'}
                        />
                    </Pressable>
                </View>

                <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 10 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <MaterialCommunityIcons name="star" size={18} color="#FFB000" />
                        <Text style={{ fontSize: 14, marginLeft: 6, color: '#333' }}>
                            {hasRatings ? `${ratingValue.toFixed(1)} rating` : 'No ratings yet'}
                        </Text>
                    </View>
                    <Text style={{ fontSize: 14, color: '#8a8a8a', marginHorizontal: 8 }}>•</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <MaterialCommunityIcons name="message-text-outline" size={16} color="#666" />
                        <Text style={{ fontSize: 14, marginLeft: 6, color: '#333' }}>
                            {reviewCount} {reviewCount === 1 ? 'review' : 'reviews'}
                        </Text>
                    </View>
                </View>
            </View>

            {/* Website Edit Modal */}
            <GenericEditModal
                visible={showWebsiteModal}
                onClose={() => setShowWebsiteModal(false)}
                currentValue={camp.campWebsite || ''}
                onSave={(newWebsite) => {
                    setCamp(prev => ({...prev, campWebsite: newWebsite}));
                }}
                title="Edit Camp Website"
                label="Camp Website URL"
                placeholder="Enter your camp's website URL (e.g., https://www.mycamp.com)"
                helperText="Enter the full URL of your camp's website"
                maxLength={200}
                keyboardType="default"
                validation={(value) => {
                    const trimmedValue = value.trim();
                    if (!trimmedValue) {
                        return 'Website URL is required';
                    }
                    // Basic URL validation
                    const urlPattern = /^https?:\/\/.+/;
                    if (!urlPattern.test(trimmedValue)) {
                        return 'Please enter a valid URL starting with http:// or https://';
                    }
                    if (trimmedValue.length > 200) {
                        return 'Website URL cannot exceed 200 characters';
                    }
                    return '';
                }}
            />

            {/* Address Edit Modal with suggestions */}
            <Modal
                visible={showAddressModal}
                transparent={true}
                animationType="fade"
                onRequestClose={() => setShowAddressModal(false)}
            >
                <View style={stylesModal.overlay}>
                    <View style={stylesModal.modalContainer}>
                        <View style={stylesModal.header}>
                            <Text style={stylesModal.title}>Edit Camp Address</Text>
                            <Pressable onPress={() => setShowAddressModal(false)} style={stylesModal.closeButton}>
                                <MaterialCommunityIcons name="close" size={20} color="#666" />
                            </Pressable>
                        </View>

                        <View style={stylesModal.content}>
                            <Text style={stylesModal.label}>Camp Address</Text>
                            <View style={stylesModal.autocompleteWrap}>
                                <AddressAutocomplete
                                    value={addressDraft}
                                    onChangeText={(text) => {
                                        setAddressDraft(text);
                                        setAddressError('');
                                        setAddressPartsDraft(null);
                                    }}
                                    onSelectPlace={handleAddressSelect}
                                    placeholder="Search camp address..."
                                    containerStyle={{ zIndex: 10001 }}
                                />
                            </View>
                            {!!addressError && <Text style={stylesModal.errorText}>{addressError}</Text>}
                            {!addressError && (
                                <Text style={stylesModal.helperText}>
                                    Search and select an address from suggestions to update map location accurately.
                                </Text>
                            )}
                        </View>

                        <View style={stylesModal.footer}>
                            <Pressable onPress={() => setShowAddressModal(false)} style={[stylesModal.button, stylesModal.cancelButton]}>
                                <Text style={stylesModal.cancelButtonText}>Cancel</Text>
                            </Pressable>
                            <Pressable onPress={saveAddress} style={[stylesModal.button, stylesModal.saveButton]}>
                                <Text style={stylesModal.saveButtonText}>Save</Text>
                            </Pressable>
                        </View>
                    </View>
                </View>
            </Modal>

            {/* Share Modal */}
            <ShareModal
                visible={showShareModal}
                onClose={() => setShowShareModal(false)}
                title={camp.campName}
                description={getCampDescription()}
                imageUrl={camp.campImageUrls?.[0]}
                shareUrl={getShareUrl()}
            />
        </View>
    );
};

export default CampPageLocation;

const stylesModal = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: 'rgba(0,0,0,0.5)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer: {
        width: '100%',
        maxWidth: 520,
        backgroundColor: '#FFFFFF',
        borderRadius: 12,
        overflow: 'visible',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 12,
        elevation: 8,
        zIndex: 10000,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: '#e9ecef',
    },
    title: {
        fontSize: 18,
        fontWeight: '600',
        color: '#333',
        flex: 1,
        marginRight: 12,
    },
    closeButton: {
        padding: 4,
    },
    content: {
        padding: 20,
        position: 'relative',
        zIndex: 5,
    },
    autocompleteWrap: {
        position: 'relative',
        zIndex: 20,
    },
    label: {
        fontSize: 16,
        fontWeight: '500',
        color: '#333',
        marginBottom: 8,
    },
    helperText: {
        fontSize: 12,
        color: '#666',
        marginTop: 8,
        textAlign: 'center',
    },
    errorText: {
        fontSize: 12,
        color: '#dc3545',
        marginTop: 8,
        textAlign: 'center',
        fontWeight: '500',
    },
    footer: {
        flexDirection: 'row',
        padding: 20,
        paddingTop: 0,
        gap: 12,
        position: 'relative',
        zIndex: 1,
    },
    button: {
        flex: 1,
        paddingVertical: 12,
        borderRadius: 8,
        alignItems: 'center',
    },
    cancelButton: {
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#e9ecef',
    },
    cancelButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: '#666',
    },
    saveButton: {
        backgroundColor: '#007bff',
    },
    saveButtonText: {
        fontSize: 16,
        fontWeight: '500',
        color: 'white',
    },
});
